// Owns the active-format state + text-pattern state that /scan and
// /capture both need: persistence, eager worker init on select, and
// auto-revert-to-QR when the fiducial or Tesseract detector fails.
// Both surfaces share the same localStorage keys (see
// activeFormatStorage.ts), so the operator's choice persists across
// route changes.

import { useEffect, useRef, useState } from 'react';
import {
  readStoredActiveFormat,
  readStoredTextPattern,
  writeStoredActiveFormat,
  writeStoredTextPattern,
} from '../lib/activeFormatStorage';
import {
  ensureTesseract,
  subscribeTesseractStatus,
  type TesseractStatus,
} from '../lib/ensureTesseract';
import {
  ensureOpenCv,
  isFiducialFormat,
  resetOpenCvCache,
  subscribeFiducialStatus,
  type FiducialStatus,
} from '../lib/fiducialDecoder';
import { safeParseTextPattern, type ParsedTextPattern } from '../lib/textPattern';
import type { ActiveFormat } from '../types';

export interface ActiveFormatToast {
  kind: 'error';
  message: string;
}

export interface UseActiveFormatResult {
  activeFormat: ActiveFormat;
  setActiveFormat: (f: ActiveFormat) => void;
  activeFormatRef: React.MutableRefObject<ActiveFormat>;
  textPattern: string;
  setTextPattern: (s: string) => void;
  parsedTextPatternRef: React.MutableRefObject<ParsedTextPattern>;
  fiducialStatus: FiducialStatus;
  tesseractStatus: TesseractStatus;
}

/**
 * Shared active-format hook. Pass a toast callback so the auto-revert
 * paths can surface why the detector was swapped without the hook
 * needing to know how each host page renders toasts.
 */
export function useActiveFormat(
  onToast?: (t: ActiveFormatToast) => void
): UseActiveFormatResult {
  const [activeFormat, setActiveFormat] = useState<ActiveFormat>(readStoredActiveFormat);
  const activeFormatRef = useRef<ActiveFormat>(activeFormat);
  const [textPattern, setTextPattern] = useState<string>(readStoredTextPattern);
  const parsedTextPatternRef = useRef<ParsedTextPattern>(safeParseTextPattern(textPattern));
  const [fiducialStatus, setFiducialStatus] = useState<FiducialStatus>({ state: 'idle' });
  const [tesseractStatus, setTesseractStatus] = useState<TesseractStatus>({ state: 'idle' });

  // Persist + eager-init on format change.
  useEffect(() => {
    activeFormatRef.current = activeFormat;
    writeStoredActiveFormat(activeFormat);
    if (isFiducialFormat(activeFormat)) {
      ensureOpenCv().catch(() => undefined);
    }
    if (activeFormat === 'text') {
      ensureTesseract().catch(() => undefined);
    }
  }, [activeFormat]);

  // Update parsed pattern ref + persist on text-pattern change.
  useEffect(() => {
    parsedTextPatternRef.current = safeParseTextPattern(textPattern);
    writeStoredTextPattern(textPattern);
  }, [textPattern]);

  useEffect(() => subscribeFiducialStatus(setFiducialStatus), []);
  useEffect(() => subscribeTesseractStatus(setTesseractStatus), []);

  // Auto-revert: fiducial error while a fiducial format is active.
  useEffect(() => {
    if (!isFiducialFormat(activeFormat)) return;
    if (fiducialStatus.state !== 'error') return;
    resetOpenCvCache();
    setActiveFormat('qr');
    onToast?.({
      kind: 'error',
      message: `Fiducial detector unavailable — reverted to QR (${fiducialStatus.message}).`,
    });
  }, [activeFormat, fiducialStatus, onToast]);

  // Auto-revert: Tesseract failure while text format is active.
  useEffect(() => {
    if (activeFormat !== 'text') return;
    if (tesseractStatus.state !== 'failed') return;
    setActiveFormat('qr');
    onToast?.({
      kind: 'error',
      message: `OCR detector unavailable — reverted to QR (${tesseractStatus.message}).`,
    });
  }, [activeFormat, tesseractStatus, onToast]);

  return {
    activeFormat,
    setActiveFormat,
    activeFormatRef,
    textPattern,
    setTextPattern,
    parsedTextPatternRef,
    fiducialStatus,
    tesseractStatus,
  };
}
